import { useAuthStore } from "@features/auth/store/authStore";
import { UserRole } from "@features/settings/types/settings.types";
import UpvoteButton from "@shared/components/UpvoteButton";
import { getProfileImage } from "@shared/hooks/useProfile";
import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useSpacesStore } from "../store/useSpacesStore";
import { Space } from "../types/space.types";
import SpaceMetadata from "./SpaceMetadata";

interface SpaceEntriesProps {
  spaces: Space[];
}

function SpaceEntries({ spaces }: SpaceEntriesProps) {
  const { toggleUpvote, toggleFollow, togglePin } = useSpacesStore();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === UserRole.ADMIN;

  const handleUpvote = (spaceId: string) => {
    toggleUpvote(spaceId);
  };

  const handleFollow = (spaceId: string) => {
    toggleFollow(spaceId);
  };

  const handlePin = (spaceId: string) => {
    togglePin(spaceId);
  };

  return (
    <>
      {spaces.map((space) => (
        <article
          key={space.id}
          className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-5"
        >
          <div className="flex flex-start space-x-4">
            {/* Avatar */}
            <div className="shrink-0 mt-1.5">
              <img
                className="w-8 h-8 rounded-full"
                src={getProfileImage(space.author)}
                width="32"
                height="32"
                alt={space.author.name || "User avatar"}
              />
            </div>
            {/* Content */}
            <div className="grow">
              {/* Title */}
              <h2 className="font-semibold text-gray-800 dark:text-gray-100 font-madefor flex items-center gap-1.5">
                <Link to={`/spaces/${space.id}`}>{space.title}</Link>
                {space.isPrivate && (
                  <Lock size={14} className="text-violet-500 shrink-0" />
                )}
              </h2>

              {/* Poll indicator */}
              {space.poll && (
                <Link
                  to={`/spaces/${space.id}`}
                  className="inline-flex items-center gap-1 mt-1 text-xs text-blue-500 font-medium"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  {pollLabel(space.poll)}
                </Link>
              )}

              {/* Footer */}
              <SpaceMetadata
                space={space}
                onFollow={(spaceId) => handleFollow(spaceId)}
              />
            </div>

            {/* Actions: Pin (admin only) + Upvote */}
            <div className="flex items-start gap-2 shrink-0">
              {isAdmin && (
                <div className="shrink-0">
                  <button
                    onClick={() => handlePin(space.id || "")}
                    title={space.isPinned ? "Unpin" : "Pin to top"}
                    className={`text-xs font-semibold text-center h-12 w-12 border rounded-lg flex flex-col justify-center items-center transition-colors ${space.isPinned
                      ? "border-blue-500/60 shadow-blue-500/20"
                      : "border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                  >
                    <svg
                      className={`w-4 h-4 mt-1.5 mb-1 ${space.isPinned
                        ? "fill-blue-500"
                        : "fill-gray-400 dark:fill-gray-500"
                        }`}
                      viewBox="0 0 24 24"
                    >
                      <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                    </svg>
                  </button>
                </div>
              )}
              <UpvoteButton
                count={space.upvotes}
                isUpvoted={space.isUpvoted}
                onClick={() => handleUpvote(space.id || "")}
              />
            </div>
          </div>
        </article>
      ))}
    </>
  );
}

export default SpaceEntries;

/** A poll with several questions says so in the list. */
function pollLabel(poll: { questions?: unknown[] } | undefined): string {
  const count = Array.isArray(poll?.questions) ? poll!.questions!.length : 1;
  return count > 1 ? `Poll · ${count} questions` : "Poll";
}
